import React, { useEffect, useState, useRef, type MouseEvent as ReactMouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import type { NavigationOptions } from "swiper/types";
import LoadingSpinner from "../LoadingSpinner";
import type { Category } from "../../types";
import '../../assets/css/theme.css';
import '../../assets/css/landing.css';

const Categories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError]           = useState<string>("");
  const [loading, setLoading]       = useState(true);
  const [activeIdx, setActiveIdx]   = useState(0);
  const [dialogCat, setDialogCat]   = useState<Category | null>(null);

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();
  const token    = localStorage.getItem("authToken");

  /* ── Close dialog on Escape key ── */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDialogCat(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  /* ── Lock body scroll when dialog is open ── */
  useEffect(() => {
    if (dialogCat) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [dialogCat]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const response = await fetch(API_ENDPOINTS.CATEGORY, { method: "POST", headers });
        const data     = await response.json();

        if (response.ok && data.status && Array.isArray(data.data)) {
          setCategories(data.data);
        } else {
          setCategories([]);
          setError(data.message || "Failed to fetch categories.");
        }
      } catch {
        setCategories([]);
        setError("An error occurred while fetching categories.");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [token]);

  const getImageUrl = (c_image?: string | null) => {
    if (!c_image) return "/placeholder.png";
    return `${IMAGE_BASE_URL}${c_image}`;
  };

  const openDialog = (e: ReactMouseEvent, cat: Category) => {
    e.stopPropagation();
    setDialogCat(cat);
  };

  const closeDialog = () => setDialogCat(null);

  const handleExplore = () => {
    if (!dialogCat) return;
    navigate(`/collection/${dialogCat.c_slug}`);
    closeDialog();
  };

  if (loading) return <LoadingSpinner />;
  if (error)   return <div className="ffh-error">{error}</div>;

  return (
    <>
      <section className="ffh-cat" id="collections">
        <div className="ffh-container">

          {/* ── Section header ── */}
          <div className="ffh-section-head">
            <p className="ffh-eyebrow">Curated Collections</p>
            <h2 className="ffh-title">New <em>Collections</em></h2>
            <p className="ffh-lead">
              Handpicked styles crafted with intention — discover each world.
            </p>
          </div>

          {/* ── Slider ── */}
          <div className="ffh-cat-slider">
            {/* Controls – rendered BEFORE <Swiper> so the refs exist when Swiper inits; CSS `order` moves them below */}
            <div className="ffh-cat-controls">
              <button ref={prevRef} type="button" className="ffh-round-btn" aria-label="Previous">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="ffh-dots">
                {categories.map((_, i) => (
                  <span key={i} className={`ffh-dot${i === activeIdx ? ' is-active' : ''}`} />
                ))}
              </div>
              <button ref={nextRef} type="button" className="ffh-round-btn" aria-label="Next">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M7 4L12 9L7 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
              onInit={(swiper) => {
                const nav = swiper.params.navigation as NavigationOptions;
                nav.prevEl = prevRef.current;
                nav.nextEl = nextRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
              }}
              onSlideChange={(swiper) => setActiveIdx(swiper.realIndex)}
              autoplay={{ delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }}
              spaceBetween={28}
              slidesPerView={3}
              breakpoints={{
                0:    { slidesPerView: 1.15, spaceBetween: 16 },
                600:  { slidesPerView: 2,    spaceBetween: 20 },
                1024: { slidesPerView: 3,    spaceBetween: 28 },
              }}
              style={{ padding: '14px 4px 6px' }}
            >
              {categories.map((cat, idx) => (
                <SwiperSlide key={cat.c_id}>
                  <div
                    className={`ffh-cat-card${idx === activeIdx ? ' is-active' : ''}`}
                    onClick={() => navigate(`/collection/${cat.c_slug}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/collection/${cat.c_slug}`)}
                  >
                    <div className="ffh-cat-media">
                      <img
                        src={getImageUrl(cat.c_image)}
                        alt={cat.c_name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
                      />
                      <span className="ffh-cat-index">0{idx + 1}</span>
                      <span className="ffh-cat-go" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                          <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>

                    <div className="ffh-cat-body">
                      <h3 className="ffh-cat-name">{cat.c_name}</h3>
                      {cat.c_description && (
                        <>
                          <p className="ffh-cat-desc">{cat.c_description}</p>
                          <button
                            type="button"
                            className="ffh-cat-more"
                            onClick={(e) => openDialog(e, cat)}
                            aria-label={`Read more about ${cat.c_name}`}
                          >
                            Read more +
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* ════════════ DESCRIPTION DIALOG ════════════ */}
      {dialogCat && (
        <div
          className="ffh-dlg-backdrop"
          onClick={closeDialog}
          role="dialog"
          aria-modal="true"
          aria-label={dialogCat.c_name}
        >
          <div className="ffh-dlg" onClick={(e) => e.stopPropagation()}>
            <div className="ffh-dlg-img">
              <img
                src={getImageUrl(dialogCat.c_image)}
                alt={dialogCat.c_name}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
              />
            </div>

            <div className="ffh-dlg-body">
              <button type="button" className="ffh-dlg-close" onClick={closeDialog} aria-label="Close dialog">
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <p className="ffh-eyebrow ffh-eyebrow--left">Collection</p>
              <h2 className="ffh-dlg-title">{dialogCat.c_name}</h2>
              <p className="ffh-dlg-desc">{dialogCat.c_description}</p>

              <div>
                <button type="button" className="ffh-btn" onClick={handleExplore}>
                  Explore collection
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Categories;